/**
 * Money handling for TeleStore.
 *
 * Every monetary value in the system is an INTEGER number of *minor units*:
 *   USD -> cents      (100 minor units = $1)
 *   KHR -> riel       (1 minor unit  = ៛1, no sub-unit exists)
 *
 * Never use floats for money. Never store "12.65" as a string in the DB.
 * The DB column type is bigint; drizzle maps it to a JS number (safe: an order
 * would need to exceed 9e15 riel to lose precision).
 *
 * KHR has an extra rule from CLAUDE.md §6.4: displayed / charged KHR amounts
 * round to the nearest 100 riel, because notes below 100៛ are not in circulation.
 */

export const CURRENCIES = ['USD', 'KHR'] as const;
export type Currency = (typeof CURRENCIES)[number];

export function isCurrency(value: unknown): value is Currency {
  return typeof value === 'string' && (CURRENCIES as readonly string[]).includes(value);
}

/** Number of decimal places the currency is quoted in. */
export const CURRENCY_DECIMALS: Record<Currency, number> = {
  USD: 2,
  KHR: 0,
};

/** Smallest amount a customer can actually be asked to pay, in minor units. */
export const CURRENCY_CASH_STEP: Record<Currency, number> = {
  USD: 1, // 1 cent
  KHR: 100, // 100 riel — §6.4
};

/** ISO 4217 numeric codes, needed by the EMVCo/KHQR payload (tag 53). */
export const CURRENCY_NUMERIC: Record<Currency, string> = {
  USD: '840',
  KHR: '116',
};

export const CURRENCY_SYMBOL: Record<Currency, string> = {
  USD: '$',
  KHR: '៛',
};

export interface Money {
  /** Integer amount in minor units. */
  readonly amount: number;
  readonly currency: Currency;
}

export function money(amount: number, currency: Currency): Money {
  assertMinorUnits(amount);
  return { amount, currency };
}

export function zero(currency: Currency): Money {
  return { amount: 0, currency };
}

export function assertMinorUnits(amount: number): void {
  if (!Number.isFinite(amount) || !Number.isInteger(amount)) {
    throw new TypeError(`Money amount must be an integer in minor units, got ${amount}`);
  }
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new TypeError(`Currency mismatch: ${a.currency} vs ${b.currency}`);
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount + b.amount, currency: a.currency };
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount - b.amount, currency: a.currency };
}

export function sum(items: readonly Money[], currency: Currency): Money {
  return items.reduce<Money>((acc, m) => add(acc, m), zero(currency));
}

/** Multiply by an integer quantity. Quantities are never fractional in v1. */
export function multiply(m: Money, quantity: number): Money {
  if (!Number.isInteger(quantity)) {
    throw new TypeError(`Quantity must be an integer, got ${quantity}`);
  }
  return { amount: m.amount * quantity, currency: m.currency };
}

/**
 * Apply a percentage (e.g. 15 for 15%) and round half-up to the currency's
 * minor unit. Used by the v2 promotion engine; lives here so there is exactly
 * one rounding rule in the codebase.
 */
export function percentOf(m: Money, percent: number): Money {
  const raw = (m.amount * percent) / 100;
  return { amount: Math.round(raw), currency: m.currency };
}

export function negate(m: Money): Money {
  return { amount: -m.amount, currency: m.currency };
}

export function isZero(m: Money): boolean {
  return m.amount === 0;
}

export function isNegative(m: Money): boolean {
  return m.amount < 0;
}

export function compare(a: Money, b: Money): number {
  assertSameCurrency(a, b);
  return a.amount === b.amount ? 0 : a.amount < b.amount ? -1 : 1;
}

export function maxMoney(a: Money, b: Money): Money {
  return compare(a, b) >= 0 ? a : b;
}

/**
 * Round an amount to something a customer can physically hand over.
 * USD is already exact at 1 cent; KHR snaps to the nearest 100 riel.
 */
export function roundToCashStep(m: Money, mode: 'nearest' | 'up' | 'down' = 'nearest'): Money {
  const step = CURRENCY_CASH_STEP[m.currency];
  if (step === 1) return m;
  const q = m.amount / step;
  const rounded = mode === 'up' ? Math.ceil(q) : mode === 'down' ? Math.floor(q) : Math.round(q);
  return { amount: rounded * step, currency: m.currency };
}

/** Parse a human-typed amount ("12.65", "12,000") into minor units. */
export function parseAmount(input: string | number, currency: Currency): Money {
  const raw = typeof input === 'number' ? String(input) : input.trim().replace(/[,\s]/g, '');
  if (raw === '' || !/^-?\d*(\.\d+)?$/.test(raw)) {
    throw new TypeError(`Cannot parse amount: ${JSON.stringify(input)}`);
  }
  const decimals = CURRENCY_DECIMALS[currency];
  const value = Number(raw);
  const minor = Math.round(value * 10 ** decimals);
  return { amount: minor, currency };
}

/** Convert minor units back to a decimal number (for JSON APIs / QR payloads). */
export function toDecimal(m: Money): number {
  const decimals = CURRENCY_DECIMALS[m.currency];
  return m.amount / 10 ** decimals;
}

/** The exact decimal string the KHQR payload expects in tag 54. */
export function toPayloadString(m: Money): string {
  const decimals = CURRENCY_DECIMALS[m.currency];
  return (m.amount / 10 ** decimals).toFixed(decimals);
}

export interface FormatOptions {
  /** 'en' uses "$12.65"; 'km' uses Khmer conventions but keeps Latin digits. */
  locale?: 'en' | 'km';
  /** Omit the currency symbol (used inside tables where the header carries it). */
  bare?: boolean;
}

export function formatMoney(m: Money, options: FormatOptions = {}): string {
  const decimals = CURRENCY_DECIMALS[m.currency];
  const negative = m.amount < 0;
  const abs = Math.abs(m.amount);
  const whole = Math.floor(abs / 10 ** decimals);
  const frac = abs % 10 ** decimals;
  const groupedWhole = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = decimals > 0 ? `${groupedWhole}.${String(frac).padStart(decimals, '0')}` : groupedWhole;
  if (options.bare) return negative ? `-${body}` : body;

  const symbol = CURRENCY_SYMBOL[m.currency];
  // USD is symbol-prefixed everywhere; riel is written after the number in Khmer.
  const text = m.currency === 'USD' ? `${symbol}${body}` : `${body}${symbol}`;
  return negative ? `-${text}` : text;
}

/**
 * Platform-supplied reference conversion (§6.4 / §6.8: merchants never type
 * their own rate). `rate` is riel per one US dollar, e.g. 4100.
 */
export function convert(m: Money, to: Currency, usdToKhrRate: number): Money {
  if (m.currency === to) return m;
  if (!Number.isFinite(usdToKhrRate) || usdToKhrRate <= 0) {
    throw new TypeError(`Invalid USD→KHR rate: ${usdToKhrRate}`);
  }
  if (m.currency === 'USD' && to === 'KHR') {
    // cents -> riel
    const riel = (m.amount / 100) * usdToKhrRate;
    return roundToCashStep({ amount: Math.round(riel), currency: 'KHR' });
  }
  // riel -> cents
  const cents = (m.amount / usdToKhrRate) * 100;
  return { amount: Math.round(cents), currency: 'USD' };
}
